import { difference, isEqual, sortBy, uniq } from 'lodash-es';
import type { IdentifiedMaps } from './types';
import { maps } from './constants';
import * as z from 'zod';
import { makeChainable } from '../common/make-chainable';
export type { MapInstance, MapName, IdentifiedMaps } from './types';
export { maps } from './constants';


function keysOf (docs: unknown): string[] {
  return makeChainable(docs)
    .chain(
      x => Array.isArray(x) ? x as unknown[] : [x],
      x => x.flatMap(x => x && typeof x === 'object' ? Object.keys(x) : []),
      x => uniq(x)
    )
    .value;
}

function sameMajorStructure (a: string, b: string): boolean {
  const normalize = (s: string) => {
    // Matches: "<prefix>-<major>[.<minor>[.<patch>]]", optional leading 'v'
    const m = s.match(/^(.*?)-v?(\d+)(?:\.\d+){0,2}$/i);
    return m ? `${m[1]}-${m[2]}` : s;
  };
  return normalize(a) === normalize(b);
}

export function identify (schedule: unknown): IdentifiedMaps {
  const similarities: IdentifiedMaps = {
    none:   [],
    weak:   [],
    strong: [],
    error:  [],
  };

  if (!schedule) return similarities;

  ////
  //// check if the schedule is a meta tagged file and thus identifiable by its structure.
  ////
  const MetaStructure = z.object({
    meta: z.union([
      z.object({
        structure: z.string()
      }),
      z.array(z.unknown())
        .transform(arr => arr.at(0))
        .pipe(z.object({
          structure: z.string()
        }))
    ])
  });
  const res = MetaStructure.safeParse(schedule);
  if (res.data) {
    // structure on the type "RS/mapName-X.Y.Z"
    const determined = maps.find(x => sameMajorStructure(x.meta.structure, res.data.meta.structure));
    if (determined) {
      similarities.determined = determined;
      return similarities;
    }
  }


  ////
  //// not a meta tagged file, so we check the structure.
  ////
  const collections: string[] = Array.isArray(schedule) ? keysOf(schedule) : Object.keys(schedule);
  if (!collections.length) return similarities;

  maps.forEach(mapping => {
    const { structure } = mapping;

    try {
      if ('matchType' in mapping && typeof schedule === mapping.matchType) {
        similarities.strong.push(mapping);
        return;
      }

      if (!structure) {
        similarities.none.push(mapping);
        return;
      }

      const _collections = Array.isArray(structure) ? structure : Object.keys(structure);

      /*
        is shallow structure .
      */
      if (Array.isArray(schedule)) {

        if (!Array.isArray(structure)) {
          similarities.none.push(mapping);
          return;
        }

        if (isEqual(sortBy(_collections), sortBy(collections))) {
          similarities.strong.push(mapping);
          return;
        }

        if (difference(sortBy(_collections), sortBy(collections)).length < _collections.length) {
          similarities.weak.push(mapping);
          return;
        }

        similarities.none.push(mapping);
        return;
      }

      /*
        is deep structure .
      */


      /*
        every key is present in file.
      */
      if (_collections.every(collection => {
        if (!(schedule && typeof schedule == 'object' && collection in schedule)) return false;
        const scheduleKeys = keysOf(schedule[collection as keyof typeof schedule]);

        if (!(collection in structure)) return false;
        const structureKeys: string[] = structure[collection as keyof typeof structure] as string[];

        return scheduleKeys.every(key => structureKeys.includes(key));
      })) {
        similarities.strong.push(mapping);
        return;
      }

      /*
        some keys is present in file.
      */
      if (_collections.some(collection => {
        if (!(schedule && typeof schedule == 'object' && collection in schedule)) return false;
        const scheduleKeys = keysOf(schedule[collection as keyof typeof schedule]);

        if (!(collection in structure)) return false;
        const structureKeys: string[] = structure[collection as keyof typeof structure] as string[];

        return scheduleKeys.some(key => structureKeys.includes(key));
      })) {
        similarities.weak.push(mapping);
        return;
      }

      similarities.none.push(mapping);
    } catch  {
      similarities.error.push(mapping);
    }
  });

  return similarities;
}
